import React from 'react';
import KwPanel from '../../common/KwPanel';
import FormInput from '../../common/FormInput';
import FormDate from '../../common/FormDate';
import FormRichText from '../../common/FormRichText';
import icons from '../../../constants/icons';
import FormSelect from '../../common/FormSelect';
import { useTranslation } from 'react-i18next';

export interface GameFormProps {
  game: any;
  register: any;
  errors: any;
  control: any;
  clearLicenseExpires: () => void;
  companies: any;
  allowedPublishers: any;
  gameFranchises: any;
  addGameFranchise: () => void;
}

function GameForm({
  game,
  register,
  errors,
  control,
  clearLicenseExpires,
  companies,
  allowedPublishers,
  gameFranchises,
  addGameFranchise,
}: GameFormProps) {
  const { t } = useTranslation();
  return (
    <KwPanel className="mb-4 pb-3 pt-1">
      <FormInput
        register={register}
        required={true}
        error={errors.name}
        errorMessage={t('Name is required')}
        name="name"
        label={t('Title')}
        placeholder={t('Name of the game')}
        defaultValue={game.name || ''}
        autoFocus={true}
        testId="game-name"
      />
      <FormSelect
        register={register}
        error={errors.gameFranchiseId}
        name="gameFranchiseId"
        label={t('Franchise')}
        placeholder=""
        defaultValue={game.gameFranchiseId || ''}
        testId="game-franchise"
        joinedButton={true}
        joinedButtonIcon={icons.ADD}
        joinedButtonClick={addGameFranchise}
      >
        <option value="">{t('--- choose ---')}</option>
        {gameFranchises &&
          [...gameFranchises]
            .filter((x: any) => !x.isArchived || x.id === game.gameFranchiseId)
            ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
            ?.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
      </FormSelect>
      <FormInput
        register={register}
        name="appId"
        label={t('App Id')}
        placeholder={t('Optional')}
        defaultValue={game.appId || ''}
        testId="game-app-id"
      />
      <FormDate
        register={register}
        name="licenseExpires"
        label={t('License expiry date')}
        placeholder={t('Optional')}
        defaultValue={game.licenseExpires || ''}
        joinedButton={true}
        joinedButtonIcon={icons.DELETE}
        joinedButtonClassName="btn-default"
        joinedButtonClick={clearLicenseExpires}
        joinedButtonTitle={t('Clear')}
        testId='game-license-expires'
      />
      <FormSelect
        register={register}
        required={true}
        error={errors.publisherCompanyId}
        errorMessage={t('Publisher is required')}
        name="publisherCompanyId"
        label={t('Publisher')}
        placeholder=""
        defaultValue={game.publisherCompanyId || ''}
        testId="game-publisher"
      >
        <option value="">{t('--- choose ---')}</option>
        {companies &&
          allowedPublishers &&
          [...companies]
            .filter(
              (x: any) => x.isPublisher && allowedPublishers?.some((y: any) => y === x.id)
            )
            ?.sort((a: any, b: any) => a.companyName.localeCompare(b.companyName))
            ?.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.companyName}
              </option>
            ))}
      </FormSelect>
      <FormSelect
        register={register}
        error={errors.developerCompanyId}
        name="developerCompanyId"
        label={t('Developer')}
        placeholder=""
        defaultValue={game.developerCompanyId || ''}
        testId="game-developer"
      >
        <option value="">{t('--- choose ---')}</option>
        {companies &&
          [...companies]
            .filter((x: any) => x.isDeveloper)
            ?.sort((a: any, b: any) => a.companyName.localeCompare(b.companyName))
            ?.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.companyName}
              </option>
            ))}
      </FormSelect>
      <FormRichText
        control={control}
        name="notesHTML"
        label={t('Notes')}
        extraRowClasses="align-items-start"
        placeholder={t('Information, links etc.')}
        defaultValue={game.notesHTML || ''}
        testId="game-notes"
      />
    </KwPanel>
  );
}

export default GameForm;
